import type { BossDef } from './boss-base';

/**
 * B7 Deep Caller — def + tuning (behaviour in tide.ts). A water Elemental
 * that FIGHTS BACK WITH WATER: its waves curl, its wave RINGS bounce the
 * player's shots, and a hard hit makes it SPLIT into two droplets — only the
 * one carrying the glowing lantern is real; the pale water copy just
 * swallows shots until the two flow back together. docs/02 Elementals.
 * Signatures: vortex + split/merge + deflecting wave ring (always), curling
 * wave ring (P1), counter-curling double wave (P2), heavy ring + aimed (P3).
 */
export const TIDE: BossDef = {
  id: 'tide',
  nameKey: 'boss.tide',
  tier: 3,
  minRoom: 2,
  baseHp: 1400,
  radius: 44,
  bodyKey: 'boss/tide/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // The ring curls as it spreads — a slow whirlpool, not a flat wave
          type: 'radial', bullet: 'bullet/enemy/ball', count: 14, interval: 3.6,
          spinDeg: 9, speed: 95, radius: 10, damage: 1, motion: { curveDeg: 16 },
        },
      ],
    },
    {
      hpPct: 0.66,
      spawnDrones: 3, // the body sheds droplets (docs/02)
      patterns: [
        // Two rings curling against each other weave a moving lattice
        {
          type: 'radial', bullet: 'bullet/enemy/ball', count: 16, interval: 3.4,
          spinDeg: 11, speed: 105, radius: 10, damage: 1, motion: { curveDeg: 16 },
        },
        {
          type: 'radial', bullet: 'bullet/enemy/ball', count: 16, interval: 3.4,
          spinDeg: -11, phaseDeg: 11.25, speed: 70, radius: 10, damage: 1,
          motion: { curveDeg: -16 },
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ball', count: 2, spreadDeg: 10,
          interval: 2.8, telegraph: 0.4, unlockTier: 1,
          speed: 260, radius: 10, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.33,
      spawnDrones: 4,
      speedMul: 1.25,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/ring', count: 12, interval: 4.0,
          spinDeg: 15, speed: 90, radius: 10, damage: 2,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ball', count: 3, spreadDeg: 16,
          interval: 2.6, telegraph: 0.4,
          speed: 270, radius: 10, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Bubble stream: two slow arms curl out of the lantern
      tier: 2,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/ball', arms: 2, interval: 0.3,
        angularSpeed: 40, speed: 100, radius: 9, damage: 1,
      },
    },
    {
      // Riptide: a fast thin ring that cuts across the slow waves
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ball', count: 20, interval: 5.0,
        spinDeg: 9, speed: 165, radius: 9, damage: 1,
      },
    },
  ],
};

/** Tide tuning — vortex, split/merge, deflecting wave rings. */
export const TD = {
  vortexInterval: 11,
  /** Body agitates first — in-fiction telegraph */
  vortexWindup: 1.0,
  vortexTime: 3.2,
  /** px/s at point blank, fades with distance */
  vortexPull: 215,
  vortexRange: 720,
  /** Close-range bite when the vortex lets go */
  snapCount: 18,
  snapSpeed: 175,
  /** Split once this share of max HP lands since the last merge */
  splitEvery: 0.15,
  /** Droplets drift this far apart, stay split, then flow back */
  splitDist: 230,
  splitTime: 5.5,
  splitIn: 0.4,
  mergeOut: 0.7,
  /** Merge splash */
  mergeCount: 10,
  mergeSpeed: 110,
  /** The water copy: paler, see-through */
  decoyTint: 0x9fd8ff,
  decoyAlpha: 0.5,
  decoyScale: 0.9,
  /** Deflecting wave ring: lantern flares, then a ring rolls out */
  waveInterval: 6.5,
  waveWarn: 0.55,
  waveSpeed: 360,
  waveMax: 460,
  /** Shots this close to the ring's crest bounce */
  waveBand: 26,
  waveColor: 0x4ab8e8,
  waveCrest: 0xe8f6ff,
  lanternTint: 0xd8f4ff,
} as const;
